import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Star, Sparkles, PenTool, Trash2, MapPin, Gauge, BatteryMedium, Download,
  MessageCircle, UserRound,
} from 'lucide-react';
import Avatar from '../ui/Avatar.jsx';
import AvatarStack from '../ui/AvatarStack.jsx';
import Button from '../ui/Button';
import Pill from '../ui/Pill.jsx';
import AuraPill from './AuraPill';
import GoalCheck from './GoalCheck';
import { useAppMotion, EASE_EMPHASIS } from '../../hooks/useAppMotion';
import { resolveAssetUrl } from '../../utils/api';
import { formatDuration, formatJournalDate, partnerToPerson } from './journalUtils';

/**
 * Eine Zeile im Lernlogbuch (Okt 2026, „App wie Spot“, Motion-Kit `.eo-entry`).
 *
 * Ersetzt JournalSessionCard und ManualEntryCard. Vorher war jeder Eintrag
 * eine eigene Karte mit Chevron, aufgeklappt mit vier farbigen Kaesten (blau,
 * gelb, gruen, lila) — Farbe fuer Kategorie statt Bedeutung (CD Regel 6). Jetzt:
 *
 *   Titel                                   ✦ +38
 *   8.10.2026 · 38 Min · ☆ 5,0 · (E)(N)
 *   Mein erster Pull Request ist durch!     ← Georgia kursiv
 *
 * Aufgeklappt folgen „Gelernt mit“ (Tipp → Chat, wenn vernetzt, sonst die
 * Profilkarte) und ruhige Abschnitte mit kleiner Versal-Ueberschrift. Bearbeiten
 * und Loeschen stehen unten, nicht neben dem Aufklappen (App-Test 3. Okt).
 *
 * Ein frisch eingetroffener Eintrag (`fresh`) schiebt sich herein, leuchtet
 * kurz auf und zaehlt seine Aura hoch — wie im Spot: „Nach jeder Session steht
 * der Eintrag schon da.“
 */

// Der Eintrag faehrt nach 0,35 s herein (0,7 s lang); die Zahlen zaehlen,
// sobald er steht — wie im Spot.
export const FRESH_COUNT_DELAY_MS = 750;

const Marker = ({ children, tone }) => (
  <span
    className={[
      'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-px text-2xs font-medium',
      tone === 'warning'
        ? 'border-warning/40 bg-warning-soft text-foreground'
        : 'border-border bg-surface-raised text-muted-foreground',
    ].join(' ')}
  >
    {children}
  </span>
);

Marker.propTypes = { children: PropTypes.node, tone: PropTypes.oneOf(['warning']) };

const Section = ({ label, children }) => (
  <section className="border-t border-border pt-3">
    <h4 className="mb-1.5 text-2xs font-semibold uppercase tracking-[0.08em] text-faint">{label}</h4>
    {children}
  </section>
);

Section.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const Prose = ({ children }) => (
  <p className="whitespace-pre-line text-sm leading-relaxed text-foreground-2">{children}</p>
);

Prose.propTypes = { children: PropTypes.node };

const partnerTarget = partner =>
  (partner.connection?.status === 'connected' && partner.connection.chatId ? 'chat' : 'profile');

const PartnerRow = ({ partner, onOpen }) => {
  const target = partnerTarget(partner);
  const name = partner.displayName || 'Lernpartner:in';
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(partner)}
        aria-label={target === 'chat' ? `Chat mit ${name} öffnen` : `Profil von ${name} ansehen`}
        className={[
          'group flex min-h-[52px] w-full items-center gap-3 rounded-xl px-1 py-1.5 text-left touch-manipulation',
          'transition-colors duration-fast hover:bg-foreground/[0.03]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
        ].join(' ')}
      >
        <Avatar name={name} src={partner.avatar || undefined} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-title-s font-semibold text-foreground">{name}</span>
          <span className="block truncate text-meta text-muted-foreground">
            {/* Wohin der Tipp fuehrt, sagen Icon und aria-label — nicht noch
                einmal der Text (Okt 2026). */}
            {partner.grade ? `Klasse ${partner.grade}` : 'Lernpartner:in'}
          </span>
        </span>
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-chip text-foreground transition-transform duration-fast ease-spring group-active:scale-95"
          aria-hidden="true"
        >
          {target === 'chat' ? <MessageCircle className="h-4 w-4" /> : <UserRound className="h-4 w-4" />}
        </span>
      </button>
    </li>
  );
};

PartnerRow.propTypes = { partner: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

const FileList = ({ files }) => (
  <ul className="space-y-1">
    {files.map((file, index) => (
      <li key={file.id || index}>
        <a
          href={resolveAssetUrl(file.url) || '#'}
          target="_blank"
          rel="noopener noreferrer"
          download={file.name || undefined}
          className="flex min-h-[44px] items-center gap-3 rounded-lg px-1 text-sm text-foreground transition-colors duration-fast hover:bg-foreground/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <Download className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
        </a>
      </li>
    ))}
  </ul>
);

FileList.propTypes = { files: PropTypes.array.isRequired };

/** Aufgeklappter Teil einer Session-Zeile. */
const SessionDetails = ({ session, readOnly, onOpenPartner }) => {
  const navigate = useNavigate();
  const isDraft = session.feedbackStatus === 'draft';
  const isEmpty = !session.feedbackStatus;
  const partners = readOnly ? [] : (session.partners || []);
  const files = readOnly ? [] : (session.files || []);
  const goals = session.goals || [];
  const skills = session.skills || [];
  const toFeedback = (e) => {
    e.stopPropagation();
    navigate(`/session/${session.id}/feedback`);
  };

  const facts = [
    session.location ? { key: 'loc', icon: MapPin, text: session.location } : null,
    // Fokus-Score nur bei Solo-Fokus-Sessions (bei Partnersessions war er umso
    // niedriger, je mehr beide miteinander geredet haben — August 2026).
    session.isFocusMode && session.focusScore ? { key: 'focus', icon: Gauge, text: `Fokus ${session.focusScore} %` } : null,
    session.energyLevel ? { key: 'energy', icon: BatteryMedium, text: `Energie ${session.energyLevel}` } : null,
  ].filter(Boolean);

  return (
    <div className="space-y-3 px-1 pb-4">
      {!readOnly && (isEmpty || isDraft) && (
        <div className="flex flex-col gap-3 rounded-xl bg-surface-raised p-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-2">
            {isDraft
              ? `Dieser Eintrag ist noch unvollständig${session.aiDraft ? ' — TeachOtter hat einen Entwurf vorbereitet.' : '.'}`
              : 'Halte fest, was du in dieser Session gelernt hast.'}
          </p>
          <Button size="sm" onClick={toFeedback} className="shrink-0">
            Ergänzen
          </Button>
        </div>
      )}

      {partners.length > 0 && (
        <Section label="Gelernt mit">
          <ul className="-mx-1">
            {partners.map(p => <PartnerRow key={p.id} partner={p} onOpen={onOpenPartner} />)}
          </ul>
        </Section>
      )}

      {/* Lernergebnisse stehen schon als Notiz im Kopf der Zeile (aufgeklappt
          ungekuerzt) — hier nicht ein zweites Mal. */}
      {session.reflection && (
        <Section label="Reflexion"><Prose>{session.reflection}</Prose></Section>
      )}
      {goals.length > 0 && (
        <Section label="Lernziele">
          <ul className="space-y-2">
            {goals.map((goal, index) => (
              <li key={goal.id || index} className="flex items-center gap-2.5">
                <GoalCheck completed={Boolean(goal.completed)} size="sm" />
                <span className={`text-sm ${goal.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {goal.text || goal.description}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {session.nextSteps && (
        <Section label="Nächste Schritte"><Prose>{session.nextSteps}</Prose></Section>
      )}
      {skills.length > 0 && (
        <Section label="Skills">
          <div className="flex flex-wrap gap-1.5">
            {skills.map((skill, index) => (
              <Pill key={index} size="sm">{typeof skill === 'string' ? skill : skill.name}</Pill>
            ))}
          </div>
        </Section>
      )}
      {facts.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3 text-sm text-foreground-2">
          {facts.map(({ key, icon: Icon, text }) => (
            <span key={key} className="inline-flex items-center gap-1.5">
              <Icon className="h-4 w-4 text-faint" aria-hidden="true" />
              {text}
            </span>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <Section label="Dateien"><FileList files={files} /></Section>
      )}

      {!readOnly && session.feedbackStatus && !isDraft && (
        <div className="flex justify-end border-t border-border pt-2">
          <Button variant="ghost" size="sm" onClick={toFeedback} aria-label="Logbucheintrag bearbeiten">
            <PenTool className="h-4 w-4" aria-hidden="true" />
            Bearbeiten
          </Button>
        </div>
      )}
    </div>
  );
};

SessionDetails.propTypes = {
  session: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  onOpenPartner: PropTypes.func,
};

/** Aufgeklappter Teil eines eigenen Eintrags. */
const EntryDetails = ({ entry, readOnly, onEdit, onDelete }) => {
  const files = readOnly ? [] : (entry.files || []);
  const tags = entry.tags || [];
  return (
    <div className="space-y-3 px-1 pb-4">
      {entry.reflection && <Section label="Reflexion"><Prose>{entry.reflection}</Prose></Section>}
      {entry.nextSteps && <Section label="Nächste Schritte"><Prose>{entry.nextSteps}</Prose></Section>}
      {tags.length > 0 && (
        <Section label="Schlagworte">
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag, i) => <Pill key={i} size="sm">#{tag}</Pill>)}
          </div>
        </Section>
      )}
      {files.length > 0 && <Section label="Dateien"><FileList files={files} /></Section>}
      {!readOnly && (
        <div className="flex justify-end gap-1 border-t border-border pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-danger hover:bg-danger-soft"
            aria-label="Eintrag löschen"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Löschen
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            aria-label="Eintrag bearbeiten"
          >
            <PenTool className="h-4 w-4" aria-hidden="true" />
            Bearbeiten
          </Button>
        </div>
      )}
    </div>
  );
};

EntryDetails.propTypes = {
  entry: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

const JournalEntryRow = ({
  item,
  expanded,
  onToggle,
  fresh = false,
  readOnly = false,
  onEdit = () => {},
  onDelete = () => {},
  onOpenPartner = () => {},
}) => {
  const { insertRow, reduced } = useAppMotion();
  const isSession = item.type === 'session';
  const data = item.data;

  const rawDate = isSession ? (data.date || data.createdAt) : (data.occurredAt || data.createdAt);
  // Eigene Eintraege ohne Titel heissen nach ihrem Datum. Vorher stand dort
  // „Eigener Eintrag“ — neben dem gleichlautenden Marker zweimal (Okt 2026).
  const untitledEntry = !isSession && !data.title;
  const title = isSession ? (data.topic || 'Lernsession') : (data.title || formatJournalDate(rawDate));
  const duration = isSession ? data.duration : data.durationMinutes;
  const aura = isSession ? (data.auraEarned || 0) : 0;
  const partners = isSession && !readOnly ? (data.partners || []) : [];

  // Die Notiz unter dem Titel: was gelernt wurde — eigener Text vor KI-Entwurf.
  const ownNote = typeof data.learningResults === 'string' ? data.learningResults : null;
  const aiNote = isSession && typeof data.aiDraft?.learningResults === 'string' ? data.aiDraft.learningResults : null;
  const note = ownNote || aiNote;
  const noteIsAi = !ownNote && Boolean(aiNote);
  const isDraft = isSession && data.feedbackStatus === 'draft';
  const isEmpty = isSession && !data.feedbackStatus && !note;

  const ratingText = data.rating > 0
    ? data.rating.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;

  const freshMotion = fresh && !reduced
    ? {
      initial: { height: 0, opacity: 0 },
      animate: { height: 'auto', opacity: 1 },
      transition: { duration: 0.7, ease: EASE_EMPHASIS, delay: 0.35 },
    }
    : {};

  return (
    <motion.li {...freshMotion} className="relative overflow-hidden border-t border-border">
      {fresh && !reduced && (
        <motion.span
          className="pointer-events-none absolute inset-0 bg-primary-soft"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ duration: 1.6, times: [0, 0.18, 0.45, 1], delay: 0.6 }}
          aria-hidden="true"
        />
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        // Ohne Namen las ein Screenreader nur „Schaltflaeche, eingeklappt“.
        aria-label={`${title} ${expanded ? 'zuklappen' : 'aufklappen'}`}
        className={[
          'relative flex w-full items-start gap-2.5 rounded-lg px-1 py-[11px] text-left touch-manipulation',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
        ].join(' ')}
      >
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-body-s font-semibold text-foreground">{title}</span>
            {isDraft && <Marker tone="warning">Entwurf</Marker>}
            {noteIsAi && (
              <Marker>
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                KI-Vorschlag
              </Marker>
            )}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
            {!isSession && <span>Eigener Eintrag</span>}
            {!isSession && !untitledEntry && <span aria-hidden="true">·</span>}
            {!untitledEntry && <span>{formatJournalDate(rawDate)}</span>}
            {duration > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>{formatDuration(duration)}</span>
              </>
            )}
            {ratingText && (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3 w-3 text-warning" aria-hidden="true" />
                  <span className="sr-only">Bewertung:</span>
                  {ratingText}
                </span>
              </>
            )}
            {partners.length > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span className="sr-only">
                  {`mit ${partners.map(p => p.displayName).join(', ')}`}
                </span>
                <AvatarStack size="2xs" max={3} decorative people={partners.map(partnerToPerson)} />
              </>
            )}
          </span>
          {note ? (
            <span
              className={`mt-1 block font-serif text-[13px] italic leading-snug text-foreground-2 ${expanded ? '' : 'line-clamp-2'}`}
            >
              {note}
            </span>
          ) : isEmpty && (
            <span className="mt-1 block text-meta text-faint">
              {readOnly ? 'Zu dieser Session wurde noch nichts festgehalten.' : 'Noch nichts festgehalten'}
            </span>
          )}
        </span>
        {aura > 0 && (
          <AuraPill
            value={aura}
            from={fresh && !reduced ? 0 : undefined}
            delay={FRESH_COUNT_DELAY_MS}
            className="mt-px"
          />
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div key="details" {...insertRow} className="relative overflow-hidden">
            {isSession ? (
              <SessionDetails session={data} readOnly={readOnly} onOpenPartner={onOpenPartner} />
            ) : (
              <EntryDetails entry={data} readOnly={readOnly} onEdit={onEdit} onDelete={onDelete} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
};

JournalEntryRow.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.oneOf(['session', 'entry']).isRequired,
    data: PropTypes.object.isRequired,
  }).isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  fresh: PropTypes.bool,
  readOnly: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onOpenPartner: PropTypes.func,
};

export default JournalEntryRow;
